import { useId, type ReactNode } from 'react';
import { Button } from '../atoms/Button';
import { Heading } from '../atoms/Heading';
import { Icon } from '../atoms/Icon';
import { useModalDialog } from '../hooks/useModalDialog';
import { useSheetDrag } from '../hooks/useSheetDrag';
import styles from './Dialog.module.css';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** One sentence, e.g. the consequences of a deletion. */
  description?: ReactNode;
  /** Buttons at the bottom right: cancel, then the primary action. */
  actions?: ReactNode;
  /** Accessible label of the close button (no built-in texts, i18n). */
  closeLabel: string;
  /** Hide the × button, e.g. for confirmations that need a decision. */
  dismissible?: boolean;
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
}

/**
 * Modal dialog based on the native `<dialog>` element: focus trap, Escape
 * and inert background come from the browser. Focus returns to the element
 * that opened it. On phones it slides up as a bottom sheet
 * (docs/ux-richtlinien.md §6.6).
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  actions,
  closeLabel,
  dismissible = true,
  size = 'md',
  children,
}: DialogProps) {
  const ref = useModalDialog(open);
  const drag = useSheetDrag(ref, onClose);
  const titleId = useId();
  const descriptionId = useId();

  return (
    // Backdrop clicks are a mouse shortcut; keyboard users close with Escape
    // (native `cancel` event) or the close button.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      tabIndex={-1}
      className={`${styles.dialog} ${styles[size]}`}
      aria-labelledby={titleId}
      {...(description ? { 'aria-describedby': descriptionId } : {})}
      onCancel={(e) => {
        e.preventDefault();
        // A sheet opened from a form dialog must not close that one, too.
        e.stopPropagation();
        onClose();
      }}
      onClick={(e) => {
        // Click on the backdrop (outside the content box) closes the dialog.
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.content}>
        <span className={styles.handle} aria-hidden="true" {...drag} />
        <header className={styles.header} {...drag}>
          <div className={styles.titles}>
            <Heading level={2} size="lg" id={titleId}>
              {title}
            </Heading>
            {description && (
              <div id={descriptionId} className={styles.description}>
                {description}
              </div>
            )}
          </div>
          {dismissible && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={closeLabel}
              onClick={onClose}
              iconStart={<Icon name="close" size="sm" />}
              className={styles.close}
            />
          )}
        </header>
        {children && <div className={styles.body}>{children}</div>}
        {actions && <footer className={styles.actions}>{actions}</footer>}
      </div>
    </dialog>
  );
}
